<template>
  <!-- Wraps something opaque and lights a short arc of its edge under the pointer.
       Caller supplies the rounding on this root; every layer inherits it. -->
  <div class="relative" @pointerenter="track($event, true)" @pointermove="track" @pointerleave="lit = false">
    <!-- Two passes: a wide blur for the bloom, a tight one for the line itself. Both sit a pixel
         outside the slotted element, which paints over them and leaves only that pixel showing --
         that ring is the whole effect. -->
    <span
      v-for="layer in LAYERS"
      :key="layer.name"
      :class="['ai-edge', `ai-edge--${layer.name}`]"
      :style="{ opacity: lit ? 1 : 0 }">
      <span class="ai-edge__arc" :style="{ '--ai-angle': `${arcAngle}deg` }">
        <span class="ai-edge__wheel" :style="{ rotate: `${wheelAngle}deg` }"></span>
      </span>
    </span>
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref } from "vue";

const LAYERS = [{ name: "bloom" }, { name: "line" }] as const;

/** Where the lit window sits, in degrees clockwise from the mask's start angle. */
const ARC_OFFSET = 261;

/**
 * Time constants for the ease, in ms: the angle covers roughly this much of the remaining
 * distance each interval, so larger is slower. The wheel trails the arc, which smears the colours
 * out behind the direction of travel instead of carrying them along rigidly.
 */
const ARC_TAU = 220;
const WHEEL_TAU = 320;

/** Below this the two are close enough to stop asking for frames, in degrees. */
const SETTLED = 0.05;

const lit = ref(false);
const arcAngle = ref(0);
const wheelAngle = ref(0);

let target = 0;
let frame = 0;
let previous = 0;

/**
 * Eased per frame rather than per pointer event: the pointer fires at whatever rate the mouse
 * reports, so driving the angle straight from it makes the arc snap across the button.
 * `1 - e^(-dt/tau)` keeps the pace the same whether that is 60 or 120 frames a second.
 */
const step = (now: number) => {
  const elapsed = previous ? now - previous : 16;
  previous = now;

  arcAngle.value += (target - arcAngle.value) * (1 - Math.exp(-elapsed / ARC_TAU));
  wheelAngle.value += (target - wheelAngle.value) * (1 - Math.exp(-elapsed / WHEEL_TAU));

  const settled = Math.abs(target - arcAngle.value) < SETTLED && Math.abs(target - wheelAngle.value) < SETTLED;
  frame = settled ? 0 : requestAnimationFrame(step);
};

/**
 * The arc is placed by rotating a conic mask, so the pointer's *angle* around the centre is what
 * matters, not its distance. Unwrapped rather than normalised: feeding the raw -180..180 range
 * into the gradient would send it the long way round every time the pointer crossed due west.
 */
const track = (e: PointerEvent, snap = false) => {
  const { left, top, width, height } = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const fromCentre = Math.atan2(e.clientY - top - height / 2, e.clientX - left - width / 2);

  // atan2 starts due east, a conic gradient starts due north.
  const desired = (fromCentre * 180) / Math.PI + 90 - ARC_OFFSET;
  target += ((((desired - target) % 360) + 540) % 360) - 180;

  // Arriving lights the edge under the pointer rather than easing in from wherever it was left.
  if (snap) {
    arcAngle.value = target;
    wheelAngle.value = target;
  } else if (!frame) {
    previous = 0;
    frame = requestAnimationFrame(step);
  }

  lit.value = true;
};

onBeforeUnmount(() => cancelAnimationFrame(frame));
</script>

<style scoped>
.ai-edge {
  position: absolute;
  inset: -1px;
  overflow: hidden;
  border-radius: inherit;
  pointer-events: none;
  transition: opacity 200ms ease-out;
  /* Keeps the blurred, scaled wheel on its own compositor layer instead of repainting per frame. */
  backface-visibility: hidden;
  transform: translateZ(0);
}

.ai-edge--bloom {
  filter: blur(2.5px);
}

.ai-edge--line {
  filter: blur(0.75px);
}

/* Cuts the colour wheel down to one arc. Two conic masks intersect: the first is the window, the
   second trims its ends, and the stretch spreads that window along the pill rather than letting it
   stay a round wedge.
   How much edge lights up is the window's width, not `scale`: past about 6 the stretch pushes most
   of the wedge outside the clip and the arc goes faint instead of long. The trough in the second
   mask has to stay clear of the window, or it clips the leading edge straight back off. */
.ai-edge__arc {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  scale: 5.5 1.5;
  -webkit-mask-image:
    conic-gradient(from var(--ai-angle), transparent 0, transparent 65%, black 70%, black 74%, transparent 79%),
    conic-gradient(from var(--ai-angle), black 46%, transparent 50%, black 54%);
  mask-image:
    conic-gradient(from var(--ai-angle), transparent 0, transparent 65%, black 70%, black 74%, transparent 79%),
    conic-gradient(from var(--ai-angle), black 46%, transparent 50%, black 54%);
  -webkit-mask-composite: source-in, xor;
  mask-composite: intersect;
}

.ai-edge--line .ai-edge__arc {
  -webkit-mask-image:
    conic-gradient(from var(--ai-angle), transparent 0, transparent 65%, black 74.5%, transparent 78%),
    conic-gradient(from var(--ai-angle), black 46%, transparent 50%, black 54%);
  mask-image:
    conic-gradient(from var(--ai-angle), transparent 0, transparent 65%, black 74.5%, transparent 78%),
    conic-gradient(from var(--ai-angle), black 46%, transparent 50%, black 54%);
}

/* A square colour wheel, centred and turned with the mask so the arc keeps its hues. */
.ai-edge__wheel {
  position: absolute;
  inset: 0;
  inset-block-start: 50%;
  translate: 0 -50%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  backface-visibility: hidden;
  background: conic-gradient(
    var(--color-ai-blue) 0%,
    var(--color-ai) 22%,
    var(--color-ai-accent) 38%,
    var(--color-ai-magenta) 50%,
    var(--color-ai-accent) 62%,
    var(--color-ai) 78%,
    var(--color-ai-blue) 100%
  );
}
</style>
